// Where a visitor who signed up from a pasted video lands when the auth flow
// has to leave the page: the signup confirmation / magic link email, and the
// full-page Google redirect used when the sign-in popup is blocked. VideoInput
// on `/spaces` reads `?v=` once the session exists and reopens the submit
// form, and the form picks its task up from `?task=`.
//
// Built with URLSearchParams so every value is encoded exactly once: a video
// URL's own query (`watch?v=abc&t=42s`, presigned links) and `task` survive
// the trip intact.
export const buildSignupRedirectUrl = (
  origin: string,
  { videoUrl, task }: { videoUrl?: string | null; task?: string | null },
): string => {
  const url = new URL("/spaces", origin);
  if (videoUrl) url.searchParams.set("v", videoUrl);
  if (task) url.searchParams.set("task", task);
  return url.toString();
};

// What Supabase appends to a redirect target. Left in a deep link they'd
// replay: a stale `error_description` makes supabase-js treat the next
// sign-in's landing as a failed callback and skip its `code` exchange.
const AUTH_CALLBACK_PARAMS = [
  "code",
  "error",
  "error_code",
  "error_description",
  "access_token",
  "refresh_token",
  "expires_in",
  "expires_at",
  "token_type",
  "provider_token",
  "provider_refresh_token",
  "type",
  "token_hash",
];

// The `?redirect=` target for sending a signed-out visitor to `/login` from
// a protected page, keeping its query (e.g. the `?v=` above, when a signup
// email is opened in a browser that can't finish the sign-in) minus the auth
// callback params that landing left behind. Encoded for use as a single
// query value; AuthShell reads it back with parseLoginRedirectParam.
export const buildLoginRedirectParam = (
  pathname: string,
  search: string,
): string => {
  const params = new URLSearchParams(search);
  for (const key of AUTH_CALLBACK_PARAMS) params.delete(key);
  const query = params.toString();
  return encodeURIComponent(query ? `${pathname}?${query}` : pathname);
};

// Where `/login` / `/signup` send the user once signed in: the `?redirect=`
// value if it's a path on this site, else `fallback`. Anything else (another
// origin, `//host`, `javascript:`) would make the redirect an open redirect,
// or script execution through router.replace.
export const parseLoginRedirectParam = (
  raw: string | null | undefined,
  fallback = "/spaces",
): string => {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return fallback;
  try {
    const base = "https://redirect.invalid";
    const url = new URL(raw, base);
    // Judged on the parsed URL, not the raw string: backslashes, tabs and
    // dot segments (`/.//host`) all normalize into a `//host` path, which
    // the router would resolve to another origin.
    if (url.origin !== base || url.pathname.startsWith("//")) return fallback;
    return url.pathname + url.search + url.hash;
  } catch {
    return fallback;
  }
};
